/**
 * Invite a user (Phase 7b): an email and the groups to join. The server sends
 * the branded email with a one-time set-password link; the dialog says what
 * happened to it (`sent`: the answer's delivery) — including, honestly, that
 * nothing left when the stack has no mailer, or SES refused it.
 */
import type { GroupWire, InviteDeliveryWire } from '@acp/api-contract/tenancy.js';
import { emailWireSchema } from '@acp/api-contract/tenancy.js';
import { type FormEvent, useState } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { GroupChecklist } from './GroupChecklist.js';

export type InviteUserDialogProps = {
  readonly open: boolean;
  readonly groups: readonly GroupWire[];
  readonly pending: boolean;
  readonly error: string | null;
  /** The invite just sent, or null before one. */
  readonly sent: { readonly email: string; readonly delivery: InviteDeliveryWire; readonly expiresAt: string } | null;
  readonly onSubmit: (request: { readonly email: string; readonly groupIds: readonly string[] }) => void;
  readonly onOpenChange: (open: boolean) => void;
};

/** What an invite's delivery means, for the admin who sent it. */
export function deliveryMessage(delivery: InviteDeliveryWire, email: string): string {
  switch (delivery) {
    case 'email':
      return `Invite emailed to ${email}.`;
    case 'file':
      return `Invite written to the development mail directory for ${email} (no email leaves this stack).`;
    case 'none':
      return `No email was sent: this stack has no mailer configured. Resend once it has.`;
    case 'failed':
      return `The mail service refused the email to ${email} (in the SES sandbox only verified addresses receive mail). The user exists — resend once that is fixed.`;
  }
}

function InviteForm({ groups, pending, error, sent, onSubmit, onOpenChange }: Omit<InviteUserDialogProps, 'open'>) {
  const [email, setEmail] = useState('');
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const valid = emailWireSchema.safeParse(email.trim()).success;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (valid && !pending) onSubmit({ email: email.trim(), groupIds });
  };
  if (sent !== null) {
    return (
      <div className="grid gap-4">
        <DialogHeader>
          <DialogTitle>Invite sent</DialogTitle>
          <DialogDescription>The link works once, until {sent.expiresAt.slice(0, 16).replace('T', ' ')} UTC.</DialogDescription>
        </DialogHeader>
        <p role="status" className={sent.delivery === 'email' || sent.delivery === 'file' ? 'text-sm' : 'text-sm text-destructive'} data-testid="invite-delivery" data-delivery={sent.delivery}>
          {deliveryMessage(sent.delivery, sent.email)}
        </p>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </div>
    );
  }
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>Invite user</DialogTitle>
        <DialogDescription>They get an email with a one-time link to set their password (valid 72 hours).</DialogDescription>
      </DialogHeader>
      <div className="grid gap-2">
        <Label htmlFor="invite-email">Email</Label>
        <Input id="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" data-testid="invite-email" />
      </div>
      <GroupChecklist groups={groups} selected={groupIds} onChange={setGroupIds} />
      {error === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!valid || pending} data-testid="invite-submit">
          {pending ? 'Inviting…' : 'Send invite'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function InviteUserDialog(props: InviteUserDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>{props.open ? <InviteForm {...props} /> : null}</DialogContent>
    </Dialog>
  );
}
